import axios from 'axios'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Badge, Button, CloseButton, Form} from 'react-bootstrap'
import {toast} from 'react-toastify'

import {
  TELEPHONY_DRAWER_CLOSE_EVENT,
  TELEPHONY_DRAWER_OPEN_EVENT,
  TELEPHONY_DRAWER_STATE_EVENT,
  TELEPHONY_DRAWER_TOGGLE_EVENT,
  TELEPHONY_DRAWER_UPDATE_EVENT,
  TelephonyDrawerOpenDetail,
  TelephonyDrawerDiagnostics,
  TelephonyDrawerStateDetail,
  TELEPHONY_VICIDIAL_LOGOUT_EVENT,
  TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT,
  TelephonyVicidialPrepareDialDetail,
} from '../../types/telephony'
import {URL} from '../../gql/client'
import {STORAGE_NAMES} from '../../helpers/consts'
import {useTelephony} from '../../hooks/useTelephony'

const VICIDIAL_EXTERNAL_WINDOW_KEY = '__policytoolsVicidialWebphoneWindow'
const VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM = 'vicidial_drawer'

const hasMeaningfulVicidialValue = (value?: string | number | null) => {
  const normalized = String(value || '').trim().toUpperCase()
  return Boolean(normalized && !['0', 'NONE', 'NULL', 'N/A'].includes(normalized))
}

const isDispositionPendingState = (status?: string | null, realTimeSubStatus?: string | null) => {
  const statusValue = String(status || '').trim().toUpperCase()
  const subStatusValue = String(realTimeSubStatus || '').trim().toUpperCase()
  return (
    statusValue === 'DEAD' ||
    subStatusValue === 'DEAD' ||
    subStatusValue.includes('DEAD') ||
    statusValue === 'DISPO' ||
    subStatusValue === 'DISPO'
  )
}

interface VicidialStandaloneDrawerSnapshot {
  drawerState: TelephonyDrawerOpenDetail
  embeddedWebphoneUrl?: string
  pauseCodeInput?: string
  manualPhoneNumber?: string
  manualLeadId?: number | string | null
}

export const TelephonyDrawer = () => {
  const telephony = useTelephony()
  const [show, setShow] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [embeddedWebphoneUrl, setEmbeddedWebphoneUrl] = useState('')
  const [pauseCodeInput, setPauseCodeInput] = useState('')
  const [manualPhoneNumber, setManualPhoneNumber] = useState('')
  const [manualLeadId, setManualLeadId] = useState<number | string | null>(null)
  const [manualAutoDialPending, setManualAutoDialPending] = useState(false)
  const [transferPhoneNumber, setTransferPhoneNumber] = useState('')
  const [runningAction, setRunningAction] = useState('')
  const [nowTick, setNowTick] = useState(() => Date.now())
  const wasPausedRef = useRef(false)

  const wrapUpStartedAt = telephony.vicidialSession.wrapUpStartedAt
  useEffect(() => {
    if (!wrapUpStartedAt) return
    const id = window.setInterval(() => setNowTick(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [wrapUpStartedAt])
  const [drawerState, setDrawerState] = useState<TelephonyDrawerOpenDetail>({
    url: '',
    title: 'Webphone',
  })

  const isVicidialAutologinBridge = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      drawerState.url.includes('/api/telephony/vicidial/autologin')
    )
  }, [drawerState.provider, drawerState.url])

  const shouldForceExternalOpen = useMemo(() => {
    if (!drawerState.url || typeof window === 'undefined') return false
    return window.location.protocol !== 'https:' && drawerState.url.startsWith('https://')
  }, [drawerState.url])

  const isAgentScreenMode = useMemo(() => {
    return drawerState.provider === 'VICIDIAL' && drawerState.displayMode === 'agent'
  }, [drawerState.displayMode, drawerState.provider])

  const canOperateVicidial = useMemo(() => {
    return (
      drawerState.provider === 'VICIDIAL' &&
      !isAgentScreenMode &&
      telephony.config.vicidial_enabled &&
      Boolean(telephony.config.vicidial_agent_user)
    )
  }, [drawerState.provider, isAgentScreenMode, telephony.config.vicidial_agent_user, telephony.config.vicidial_enabled])

  const publishDrawerState = useCallback(
    (mounted: boolean, visible: boolean, provider?: TelephonyDrawerStateDetail['provider']) => {
      window.dispatchEvent(
        new CustomEvent<TelephonyDrawerStateDetail>(TELEPHONY_DRAWER_STATE_EVENT, {
          detail: {
            mounted,
            visible,
            provider,
          },
        })
      )
    },
    []
  )

  const openInNewTab = () => {
    if (drawerState.provider === 'VICIDIAL' && drawerState.url) {
      const snapshot: VicidialStandaloneDrawerSnapshot = {
        drawerState,
        embeddedWebphoneUrl,
        pauseCodeInput,
        manualPhoneNumber,
        manualLeadId,
      }
      localStorage.setItem(
        STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE,
        JSON.stringify(snapshot)
      )

      const targetLocation = new window.URL(window.location.href)
      targetLocation.searchParams.set(VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM, '1')
      const externalWindow = window.open(targetLocation.toString(), '_blank')
      externalWindow?.focus?.()
      return
    }

    const targetUrl = isVicidialAutologinBridge
      ? `${drawerState.url}${drawerState.url.includes('?') ? '&' : '?'}standalone=1`
      : embeddedWebphoneUrl || drawerState.url
    if (!targetUrl) return
    const externalWindow = window.open(
      targetUrl,
      isVicidialAutologinBridge ? '_blank' : 'PolicyToolsVicidialWebphone'
    )
    if (externalWindow) {
      if (!isVicidialAutologinBridge) {
        ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = externalWindow
      }
      externalWindow.focus?.()
    }
  }

  const closeTrackedExternalWindow = () => {
    let externalWindow = (window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] as Window | null | undefined
    if (!externalWindow) {
      externalWindow = window.open('', 'PolicyToolsVicidialWebphone')
    }
    if (externalWindow && !externalWindow.closed && externalWindow.location.href !== 'about:blank') {
      externalWindow.close()
    }
    ;(window as any)[VICIDIAL_EXTERNAL_WINDOW_KEY] = null
  }

  const handleVicidialLogout = async () => {
    const token = localStorage.getItem(STORAGE_NAMES.TOKEN)
    if (!token) {
      toast.error('Your session expired. Log in again to disconnect PolicyDial.')
      return
    }

    setDisconnecting(true)
    try {
      await axios.post(
        `${URL}/api/telephony/disconnect-agent`,
        {provider: 'VICIDIAL'},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      localStorage.removeItem(STORAGE_NAMES.VICIDIAL_SESSION)
      localStorage.setItem(STORAGE_NAMES.VICIDIAL_FORCE_DISCONNECTED, '1')
      window.dispatchEvent(new CustomEvent(TELEPHONY_VICIDIAL_LOGOUT_EVENT))
      
      const channel = new BroadcastChannel('policytools_vicidial_channel')
      channel.postMessage('CLOSE_WEBPHONE')
      channel.close()
      
      closeTrackedExternalWindow()
      setCollapsed(false)
      setShow(false)
      toast.info(
        shouldForceExternalOpen
          ? 'PolicyDial disconnected. If the secure PolicyDial page still remains open in another browser tab, use the native LOGOUT link there once.'
          : 'PolicyDial disconnected.'
      )
    } catch (error: any) {
      const message = error?.response?.data?.error || 'Unable to log out from PolicyDial.'
      toast.error(message)
    } finally {
      setDisconnecting(false)
    }
  }

  const runVicidialDrawerAction = async (actionKey: string, action: () => Promise<unknown>) => {
    setRunningAction(actionKey)
    try {
      await action()
    } finally {
      setRunningAction('')
    }
  }

  const handlePauseAgent = async () => {
    await runVicidialDrawerAction('pause', () =>
      telephony.setVicidialPause(true, pauseCodeInput)
    )
  }

  const handleResumeAgent = async () => {
    await runVicidialDrawerAction('resume', () => telephony.setVicidialPause(false))
  }

  const handleTransferCall = async () => {
    const phoneNumber = transferPhoneNumber.trim()
    if (!phoneNumber) {
      toast.error('Enter a transfer phone number first.')
      return
    }

    const response = await telephony.transferVicidialCall(phoneNumber)
    if (response) {
      setTransferPhoneNumber('')
    }
  }

  const handleHangupCurrentCall = async () => {
    await runVicidialDrawerAction('hangup', () =>
      telephony.hangupCall('VICIDIAL', telephony.vicidialSession.callId || null)
    )
  }

  const handleManualCall = useCallback(async () => {
    const phoneNumber = manualPhoneNumber.trim()
    if (!phoneNumber) {
      toast.error('Enter a phone number first.')
      return
    }

    await runVicidialDrawerAction('dial', async () => {
      await telephony.startCall({
        phone: phoneNumber,
        leadId: manualLeadId,
        providerOverride: 'VICIDIAL',
        autoOpenWebphone: false,
      })
    })
  }, [manualLeadId, manualPhoneNumber, telephony])

  useEffect(() => {
    const currentStatus = String(telephony.vicidialSession.status || '').trim().toUpperCase()
    const currentPauseCode = String(telephony.vicidialSession.pauseCode || '').trim().toUpperCase()
    const currentlyPaused = currentStatus === 'PAUSED'

    if (currentPauseCode) {
      setPauseCodeInput(currentPauseCode)
    } else if (wasPausedRef.current && !currentlyPaused) {
      setPauseCodeInput('')
    }

    wasPausedRef.current = currentlyPaused
  }, [telephony.vicidialSession.pauseCode, telephony.vicidialSession.status])

  useEffect(() => {
    const isStandaloneDrawerView =
      new URLSearchParams(window.location.search).get(VICIDIAL_DRAWER_STANDALONE_QUERY_PARAM) ===
      '1'
    if (isStandaloneDrawerView) {
      const storedSnapshot = localStorage.getItem(STORAGE_NAMES.VICIDIAL_DRAWER_STANDALONE_STATE)
      if (storedSnapshot) {
        try {
          const snapshot = JSON.parse(storedSnapshot) as VicidialStandaloneDrawerSnapshot
          if (snapshot?.drawerState?.url) {
            setEmbeddedWebphoneUrl(snapshot.embeddedWebphoneUrl || '')
            setPauseCodeInput(snapshot.pauseCodeInput || '')
            setManualPhoneNumber(snapshot.manualPhoneNumber || '')
            setManualLeadId(snapshot.manualLeadId ?? null)
            setManualAutoDialPending(false)
            setDrawerState(snapshot.drawerState)
            setShow(true)
            setCollapsed(false)
          }
        } catch (error) {
          console.error('Unable to restore the standalone VICIdial drawer view', error)
        }
      }
    }

    const handleOpen = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyDrawerOpenDetail>
      if (!customEvent.detail?.url) return

      const payload = { ...customEvent.detail }
      if (typeof window !== 'undefined' && window.location.protocol === 'https:' && payload.url.startsWith('http://')) {
        payload.url = payload.url.replace(/^http:\/\//i, 'https://')
      }

      setEmbeddedWebphoneUrl('')
      setDrawerState(payload)
      setShow(true)
      setCollapsed(false)
    }

    const handleUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{diagnostics?: TelephonyDrawerDiagnostics}>
      if (!customEvent.detail?.diagnostics) return

      setDrawerState((current) => ({
        ...current,
        diagnostics: {
          ...(current.diagnostics || {}),
          ...customEvent.detail.diagnostics,
        },
      }))
    }

    const handleClose = () => {
      setEmbeddedWebphoneUrl('')
      setCollapsed(false)
      setShow(false)
    }

    const handleToggle = () => {
      if (!show) {
        if (drawerState.url) {
          setShow(true)
          setCollapsed(false)
        }
        return
      }

      setCollapsed((current) => !current)
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data as {type?: string; webphoneUrl?: string} | undefined
      if (!data || data.type !== 'POLICYTOOLS_VICIDIAL_WEBPHONE_READY' || !data.webphoneUrl) {
        return
      }

      setEmbeddedWebphoneUrl(data.webphoneUrl)
      setDrawerState((current) => ({
        ...current,
        width: '460px',
        displayMode: 'webphone',
        diagnostics: {
          ...(current.diagnostics || {}),
          message: 'PolicyDial connected. Loading embedded webphone...',
        },
      }))
    }

    const handlePrepareDial = (event: Event) => {
      const customEvent = event as CustomEvent<TelephonyVicidialPrepareDialDetail>
      const phone = String(customEvent.detail?.phone || '').trim()
      if (!phone) return

      if (!telephony.vicidialSession.activeSession) {
        toast.error('Connect PolicyDial from Integrations before starting a call.')
        return
      }

      setManualPhoneNumber(phone)
      setManualLeadId(customEvent.detail?.leadId ?? null)
      setManualAutoDialPending(customEvent.detail?.autoDial !== false)
      setShow(true)
      setCollapsed(false)
    }

    window.addEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
    window.addEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
    window.addEventListener(TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT, handlePrepareDial as EventListener)
    window.addEventListener('message', handleMessage)

    const channel = new BroadcastChannel('policytools_vicidial_channel')
    channel.onmessage = (event) => {
      if (event.data === 'CLOSE_WEBPHONE') {
        closeTrackedExternalWindow()
        setEmbeddedWebphoneUrl('')
        setCollapsed(false)
        setShow(false)
      }
    }

    return () => {
      window.removeEventListener(TELEPHONY_DRAWER_OPEN_EVENT, handleOpen as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_UPDATE_EVENT, handleUpdate as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_CLOSE_EVENT, handleClose as EventListener)
      window.removeEventListener(TELEPHONY_DRAWER_TOGGLE_EVENT, handleToggle as EventListener)
      window.removeEventListener(TELEPHONY_VICIDIAL_PREPARE_DIAL_EVENT, handlePrepareDial as EventListener)
      window.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [drawerState.provider, drawerState.url, show, telephony])

  useEffect(() => {
    publishDrawerState(show, show && !collapsed, drawerState.provider)
  }, [collapsed, drawerState.provider, publishDrawerState, show])

  useEffect(() => {
    if (
      !manualAutoDialPending ||
      !manualPhoneNumber.trim() ||
      !telephony.vicidialSession.activeSession ||
      runningAction === 'dial'
    ) {
      return
    }

    setManualAutoDialPending(false)
    void handleManualCall()
  }, [
    handleManualCall,
    manualAutoDialPending,
    manualPhoneNumber,
    runningAction,
    telephony.vicidialSession.activeSession,
  ])

  const activeCallPhoneNumber = String(
    telephony.vicidialSession.phoneNumber || drawerState.diagnostics?.phone_number || ''
  ).trim()
  const activeCallVendorLeadCode = String(
    telephony.vicidialSession.vendorLeadCode || drawerState.diagnostics?.vendor_lead_code || ''
  ).trim()
  const activeCallLeadId = String(telephony.vicidialSession.leadId || '').trim()
  const activeCallSubStatus = String(
    telephony.vicidialSession.realTimeSubStatus || drawerState.diagnostics?.real_time_sub_status || ''
  )
    .trim()
    .toUpperCase()
  const effectiveVicidialStatus = useMemo(() => {
    const rawStatus = String(drawerState.diagnostics?.status || telephony.vicidialSession.status || '')
      .trim()
      .toUpperCase()
    return isDispositionPendingState(rawStatus, activeCallSubStatus) ? 'DEAD' : rawStatus
  }, [activeCallSubStatus, drawerState.diagnostics?.status, telephony.vicidialSession.status])

  const isPausedState = effectiveVicidialStatus === 'PAUSED'
  const isReadyState =
    Boolean(telephony.vicidialSession.activeSession || drawerState.diagnostics?.session_id) &&
    !isPausedState &&
    effectiveVicidialStatus !== 'DEAD'
  const isPauseActionRunning = runningAction === 'pause'
  const isResumeActionRunning = runningAction === 'resume'
  const canTogglePauseState =
    !disconnecting &&
    !telephony.vicidialSession.connecting &&
    !runningAction &&
    Boolean(telephony.vicidialSession.activeSession)
  const pauseStatusLabel = isPausedState ? 'YOU ARE PAUSED' : isReadyState ? 'YOU ARE ACTIVE' : 'SESSION NOT READY'
  const pauseActionLabel = isPausedState ? 'DIAL NEXT NUMBER' : 'ACTIVE PAUSE'
  const pauseActionBusyLabel = isPausedState ? 'GOING ACTIVE...' : 'PAUSING...'
  const pauseStatusVariant = isPausedState ? 'warning' : isReadyState ? 'success' : 'secondary'
  const pauseActionVariant = isPausedState ? 'success' : 'warning'

  const hasStructuredDiagnostics = Boolean(
    drawerState.diagnostics?.agent_user ||
      drawerState.diagnostics?.status ||
      drawerState.diagnostics?.campaign_id ||
      drawerState.diagnostics?.pause_code ||
      drawerState.diagnostics?.session_id ||
      drawerState.diagnostics?.phone_number
  )
  const dispositionPending = isDispositionPendingState(
    effectiveVicidialStatus,
    activeCallSubStatus
  )
  const hasLiveCallContext =
    !dispositionPending &&
    (
      hasMeaningfulVicidialValue(telephony.vicidialSession.callId) ||
      hasMeaningfulVicidialValue(activeCallLeadId) ||
      hasMeaningfulVicidialValue(activeCallPhoneNumber) ||
      hasMeaningfulVicidialValue(activeCallVendorLeadCode) ||
      ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => effectiveVicidialStatus.includes(marker)) ||
      ['INCALL', 'LIVE', 'QUEUE', 'RING'].some((marker) => activeCallSubStatus.includes(marker))
    )
  const activeCallBannerTitle =
    effectiveVicidialStatus === 'INCALL' || activeCallSubStatus.includes('INCALL') || activeCallSubStatus.includes('LIVE')
      ? 'PolicyDial call active'
      : 'Incoming PolicyDial call'
  const selectedPhoneNumber = manualPhoneNumber.trim()

  if (!show) return null

  return (
    <div
      role='dialog'
      aria-hidden={collapsed}
      style={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        right: 0,
        width: drawerState.width || '460px',
        maxWidth: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderLeft: '1px solid rgba(0,0,0,0.15)',
        boxShadow: '-4px 0 16px rgba(0,0,0,0.08)',
        zIndex: 1045,
        transform: collapsed ? 'translateX(100%)' : 'translateX(0)',
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? 'none' : 'auto',
        visibility: collapsed ? 'hidden' : 'visible',
        transition: 'transform 0.3s ease, opacity 0.2s ease, visibility 0.3s',
      }}
    >
      <div className='offcanvas-header border-bottom'>
        <div className='d-flex flex-column'>
          <h5 className='offcanvas-title mb-0'>{drawerState.title || 'Webphone'}</h5>
          {drawerState.warning && (
            <span className='text-muted fs-8 mt-1'>{drawerState.warning}</span>
          )}
        </div>
        <CloseButton onClick={() => setCollapsed(true)} aria-label='Close' />
      </div>
      <div className='offcanvas-body p-0 d-flex flex-column' style={{flex: 1, overflowY: 'auto'}}>
        {drawerState.provider !== 'VICIDIAL' && (
          <div className='border-bottom p-3 d-flex justify-content-end gap-2'>
            <Button
              variant='light-primary'
              size='sm'
              onClick={openInNewTab}
              disabled={!drawerState.url || disconnecting}
            >
              Open in new tab
            </Button>
          </div>
        )}
        {shouldForceExternalOpen && (
          <Alert variant='warning' className='m-3 mb-0'>
            Your current app session is running over HTTP. ViciPhone rejects embedded access from
            non-encrypted referrers like <code>localhost</code>. Use <strong>Open secure webphone</strong>{' '}
            to continue in a separate HTTPS tab.
          </Alert>
        )}
        {drawerState.diagnostics && !isAgentScreenMode && (
          <div className='border-bottom px-3 py-2 bg-light'>
            {drawerState.diagnostics.message && (
              <div className='fw-semibold mb-2'>{drawerState.diagnostics.message}</div>
            )}
            {hasStructuredDiagnostics ? (
              <div className='d-flex flex-wrap gap-2 fs-8'>
                {drawerState.diagnostics.agent_user && (
                  <Badge bg='light' text='dark' className='border'>
                    Agent: {drawerState.diagnostics.agent_user}
                  </Badge>
                )}
                {drawerState.diagnostics.status && (
                  <Badge
                    bg={isPausedState ? 'warning' : isReadyState ? 'success' : 'secondary'}
                    text={isPausedState ? 'dark' : 'white'}
                  >
                    Status: {effectiveVicidialStatus}
                  </Badge>
                )}
                {drawerState.diagnostics.campaign_id && (
                  <Badge bg='light' text='dark' className='border'>
                    Campaign: {drawerState.diagnostics.campaign_id}
                  </Badge>
                )}
                {drawerState.diagnostics.pause_code && (
                  <Badge bg='light' text='dark' className='border'>
                    Pause code: {drawerState.diagnostics.pause_code}
                  </Badge>
                )}
                {drawerState.diagnostics.session_id && (
                  <Badge bg='light' text='dark' className='border'>
                    Session: {drawerState.diagnostics.session_id}
                  </Badge>
                )}
                {drawerState.diagnostics.phone_number && (
                  <Badge bg='light' text='dark' className='border'>
                    Phone: {drawerState.diagnostics.phone_number}
                  </Badge>
                )}
              </div>
            ) : null}
            {!hasStructuredDiagnostics && drawerState.diagnostics.raw_response && (
              <pre
                className='mt-2 mb-0 p-2 rounded bg-white border'
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '120px',
                  overflow: 'auto',
                  fontSize: '11px',
                }}
              >
                {drawerState.diagnostics.raw_response}
              </pre>
            )}
          </div>
        )}
        {canOperateVicidial && hasLiveCallContext && (
          <div className='border-bottom px-3 py-3 bg-success bg-opacity-10'>
            <div className='d-flex align-items-center justify-content-between gap-2 mb-2'>
              <div className='fw-semibold text-success'>{activeCallBannerTitle}</div>
              <Button
                variant='danger'
                size='sm'
                onClick={handleHangupCurrentCall}
                disabled={disconnecting || telephony.vicidialSession.connecting || runningAction === 'hangup'}
              >
                {runningAction === 'hangup' ? 'Hanging up...' : 'Hang up'}
              </Button>
            </div>
            {(hasMeaningfulVicidialValue(telephony.vicidialSession.firstName) ||
              hasMeaningfulVicidialValue(telephony.vicidialSession.lastName)) && (
              <div className='fw-bold text-dark fs-6 mb-2'>
                {[telephony.vicidialSession.firstName, telephony.vicidialSession.lastName]
                  .filter((value) => hasMeaningfulVicidialValue(value))
                  .join(' ')}
              </div>
            )}
            <div className='d-flex flex-wrap gap-2 fs-8'>
              {hasMeaningfulVicidialValue(activeCallPhoneNumber) && (
                <Badge bg='light' text='dark' className='border'>
                  Phone: {activeCallPhoneNumber}
                </Badge>
              )}
              {hasMeaningfulVicidialValue(telephony.vicidialSession.email) && (
                <Badge bg='light' text='dark' className='border'>
                  Email: {telephony.vicidialSession.email}
                </Badge>
              )}
              {hasMeaningfulVicidialValue(activeCallVendorLeadCode) && (
                <Badge bg='light' text='dark' className='border'>
                  Vendor ID: {activeCallVendorLeadCode}
                </Badge>
              )}
              {hasMeaningfulVicidialValue(activeCallLeadId) && (
                <Badge bg='light' text='dark' className='border'>
                  Lead ID: {activeCallLeadId}
                </Badge>
              )}
              {activeCallSubStatus && (
                <Badge bg='light' text='dark' className='border'>
                  Substatus: {activeCallSubStatus}
                </Badge>
              )}
            </div>
          </div>
        )}
        {canOperateVicidial && !hasLiveCallContext && selectedPhoneNumber && (
          <div className='border-bottom px-3 py-3 bg-primary bg-opacity-10'>
            <div className='fw-semibold text-primary mb-2'>
              {runningAction === 'dial' ? 'Calling through PolicyDial' : 'Selected number'}
            </div>
            <div className='d-flex flex-wrap gap-2 fs-8 mb-2'>
              <Badge bg='light' text='dark' className='border'>
                Phone: {selectedPhoneNumber}
              </Badge>
              {manualLeadId ? (
                <Badge bg='light' text='dark' className='border'>
                  Lead ID: {manualLeadId}
                </Badge>
              ) : null}
            </div>
            <div className='text-muted fs-8 mb-0'>
              PolicyTools is using the current PolicyDial session in this drawer for the dial action.
            </div>
          </div>
        )}
        {canOperateVicidial && dispositionPending && wrapUpStartedAt && (() => {
          const elapsedSec = Math.max(0, Math.floor((nowTick - wrapUpStartedAt) / 1000))
          const minutes = Math.floor(elapsedSec / 60)
          const seconds = elapsedSec % 60
          return (
            <div className='border-bottom px-3 py-2 bg-warning bg-opacity-25 d-flex align-items-center gap-2'>
              <i className='bi bi-hourglass-split text-warning' />
              <span className='fw-semibold small'>Wrap-up:</span>
              <span className='fw-bold'>{`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}</span>
              <span className='text-muted small ms-auto'>Submit a disposition to release the call.</span>
            </div>
          )
        })()}
        {canOperateVicidial && (
          <div className='border-bottom px-3 py-3 bg-white'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <div className='fw-semibold'>Agent Controls</div>
              {telephony.vicidialSession.callsInQueueCount > 0 && (
                <Badge bg='info' className='text-uppercase'>
                  <i className='bi bi-people-fill me-1' />
                  {telephony.vicidialSession.callsInQueueCount} in queue
                </Badge>
              )}
            </div>
            <div className='d-flex flex-column gap-2'>
              <div className='d-flex gap-2 flex-wrap'>
                <Form.Control
                  size='sm'
                  placeholder='Pause code'
                  value={pauseCodeInput}
                  maxLength={6}
                  onChange={(event) => setPauseCodeInput(event.target.value.toUpperCase())}
                  style={{maxWidth: '120px'}}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                />
                <Button
                  variant={pauseStatusVariant}
                  size='sm'
                  disabled={
                    true
                  }
                  style={{minWidth: '150px'}}
                  className='fw-bold text-uppercase'
                >
                  {pauseStatusLabel}
                </Button>
                <Button
                  variant={pauseActionVariant}
                  size='sm'
                  onClick={isPausedState ? handleResumeAgent : handlePauseAgent}
                  disabled={!canTogglePauseState}
                  style={{minWidth: '160px'}}
                  className='fw-bold text-uppercase'
                >
                  {isPauseActionRunning || isResumeActionRunning ? pauseActionBusyLabel : pauseActionLabel}
                </Button>
              </div>
              <div className='d-flex gap-2 flex-wrap'>
                <Form.Control
                  size='sm'
                  placeholder='Blind transfer number'
                  value={transferPhoneNumber}
                  onChange={(event) => setTransferPhoneNumber(event.target.value)}
                  style={{minWidth: '220px', flex: '1 1 220px'}}
                  disabled={disconnecting || telephony.vicidialSession.connecting}
                />
                <Button
                  variant='outline-primary'
                  size='sm'
                  onClick={() => runVicidialDrawerAction('transfer', handleTransferCall)}
                  disabled={
                    disconnecting ||
                    telephony.vicidialSession.connecting ||
                    runningAction === 'transfer' ||
                    !telephony.vicidialSession.activeSession
                  }
                >
                  {runningAction === 'transfer' ? 'Sending...' : 'Blind Transfer'}
                </Button>
              </div>
            </div>
          </div>
        )}
        {drawerState.url && !shouldForceExternalOpen ? (
          <>
            <iframe
              src={drawerState.url}
              title={drawerState.title || 'Webphone'}
              allow='microphone; autoplay'
              referrerPolicy='no-referrer'
              style={
                embeddedWebphoneUrl && isVicidialAutologinBridge
                  ? {
                      border: 0,
                      width: '1px',
                      height: '1px',
                      opacity: 0,
                      pointerEvents: 'none',
                      position: 'absolute',
                    }
                  : {border: 0, width: '100%', height: '100%'}
              }
            />
            {embeddedWebphoneUrl && isVicidialAutologinBridge ? (
              <iframe
                src={embeddedWebphoneUrl}
                title='PolicyDial Embedded Webphone'
                allow='microphone; autoplay'
                referrerPolicy='no-referrer'
                style={{border: 0, width: '100%', height: '100%'}}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}
